import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';

import withContainerErrorBoundary from 'components/ErrorBoundaries/ContainerErrorBoundary/withContainerErrorBoundary';
import ActivityPerms from 'components/AccessControl/permissions.json';
import AccessControl from 'components/AccessControl';
import ReactTable from 'components/ReactTable';
import ChangeOwnershipModal from 'components/Modals/ChangeOwnershipModal';
import AssignToUserModal from 'components/Modals/AssignToUserModal';

import SearchPanel from 'components/Monitoring/SearchPanel';
import ActionSelect from 'components/Monitoring/ActionSelect';
import CicColumnDefs from 'components/Monitoring/CicColumnDefinitions';
import CicActions from 'components/Monitoring/GridActions';
import CicAlertModal from 'components/Modals/CicAlertModal';

import {
  getStorageSetting,
  getCicAlertsByObject,
  getUserCicAlertsByObject,
  getWorkflowCicAlertsByObject,
  getCicAlertReportFile,
  saveCicAlertCriteria,
  showCicAlertModal,
  hideCicAlertModal,
  takeCicAlert,
  showOwnershipModal,
  hideOwnershipModal,
  showAssignCicToUserModal,
  hideAssignCicToUserModal,
  assignCicsToUserWithCallback,
  getCicAlertDetails,
  clearCicAlertDetails,
  getCicAlertCounts,
} from 'actions';

const WAIT_INTERVAL = 500;

class Monitoring extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string),
    initSearchCriteria: PropTypes.shape(),
    initGridCriteria: PropTypes.shape(),
    gridTotalLimit: PropTypes.number.isRequired,
    cicAlertsListCount: PropTypes.number.isRequired,
    cicAlertsList: PropTypes.arrayOf(PropTypes.shape()),
    isCicAlertModalShown: PropTypes.bool,
    isAssignCicToUserModalShown: PropTypes.bool,
    cicAlertId: PropTypes.number,
    changeOwnershipItem: PropTypes.shape(),
    isOwnershipModalShown: PropTypes.bool,
    refreshPage: PropTypes.bool,
    cicAlertList: PropTypes.arrayOf(PropTypes.number),
    isGettingCicAlertXls: PropTypes.bool
  };

  static defaultProps = {
    permissions: null,
    initSearchCriteria: null,
    initGridCriteria: null,
    cicAlertsList: [],
    isCicAlertModalShown: true,
    isAssignCicToUserModalShown: false,
    cicAlertId: null,
    refreshPage: false,
    changeOwnershipItem: null,
    isOwnershipModalShown: false,
    cicAlertList: [],
    isGettingCicAlertXls: false
  };

  static isUserOwner(user) {
    const loggedOnUser = getStorageSetting('username');

    return (loggedOnUser != null && user != null && loggedOnUser === user);
  }

  constructor(props) {
    super(props);

    const { initGridCriteria, initSearchCriteria, permissions, gridTotalLimit } = props;
    const searchCriteria = initSearchCriteria || {
      searchQuery: '',
      legalEntity: '',
      legalEntitySelected: false,
      accountNumber: '',
      accountNumberSelected: false,
      customer: '',
      customerSelected: false,
      workflowType: 'myalerts'
    };
    const gridCriteria = initGridCriteria || {
      columnDefs: CicColumnDefs(permissions).concat([]),
      pageIndex: 0,
      sortBy: [],
      filters: [],
      pageCount: 0,
      pageSize: 50,
      totalLimit: gridTotalLimit,
      itemCount: 0,
      selectedItems: [],
      selectedItem: null
    };

    this.state = {
      data: [],
      initialLoaded: false,
      loading: false,
      loadingTime: 500,
      searchCriteria,
      gridCriteria,
      selectionEnabled: true,
    };
  }

  /* eslint-disable-next-line camelcase */
  UNSAFE_componentWillMount() {
    this.updateGridCriteriaState();
  }

  componentDidMount() {
    this.sendDataRequest();
    this.updateNotificationHeader();
  }

  /* eslint-disable react/no-did-update-set-state */
  componentDidUpdate(prevProps) {
    const { initialLoaded } = this.state;
    const { refreshPage, cicAlertsList, cicAlertsListCount } = this.props;

    if (refreshPage) {
      this.sendDataRequest();
    }

    if (cicAlertsList !== prevProps.cicAlertsList) {
      this.processData(cicAlertsList, cicAlertsListCount);
      this.hideLoadingAnimation();
      if (!initialLoaded) {
        this.setState({ initialLoaded: true });
      }
    }
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    const { gridCriteria, searchCriteria } = this.state;
    dispatch(saveCicAlertCriteria(searchCriteria, gridCriteria));
  }

  get isSearchCriteriaValid() {
    const { searchCriteria } = this.state;

    const isEmpty = searchCriteria
      && !searchCriteria.customer
      && !searchCriteria.legalEntity
      && !searchCriteria.accountNumber;

    return (searchCriteria
      && (isEmpty || searchCriteria.customerSelected || searchCriteria.legalEntitySelected || searchCriteria.accountNumberSelected
        || searchCriteria.customer?.length >= 3 || searchCriteria.legalEntity?.length >= 3 || searchCriteria.accountNumber?.length >= 3));
  }

  updateGridCriteriaState = () => {
    const { gridCriteria } = this.state;
    const { permissions } = this.props;
    const columnDefs = CicColumnDefs(permissions);
    this.setState({ gridCriteria: { ...gridCriteria, columnDefs } });
  };

  hideLoadingAnimation = () => {
    const { loadingTime } = this.state;
    const loadingInterval = setInterval(() => {
      this.setState({ loading: false });
      clearInterval(loadingInterval);
    }, loadingTime);
  };

  processData = (items, itemCount) => {
    const { gridCriteria } = this.state;
    const { gridTotalLimit } = this.props;
    const data = [];
    if (items != null) {
      items.forEach((item) => {
        const datum = {
          // cic alerts
          itemId: item?.id,
          accountId: item?.accountId,
          customerDbId: item?.customerDbId,
          referenceCode: (item?.referenceCode == null || item?.referenceCode === '') ? 'N/A' : item?.referenceCode,
          customerId: (item?.customerId == null || item?.customerId === '') ? 'N/A' : item?.customerId,
          customerName: (item?.customerName == null || item?.customerName === '') ? 'N/A' : item?.customerName,
          cicActionRequired: (item?.actionRequired == null || item?.actionRequired === '') ? 'N/A' : item?.actionRequired,
          createdDate: (item?.dateCreated == null || item?.dateCreated === '') ? 'N/A' : moment(item?.dateCreated).format('MMM DD, YYYY'),
          daysToExpiration: (item?.daysBeforeExpiration == null || item?.daysBeforeExpiration === '') ? 'N/A' : item?.daysBeforeExpiration,
          cicDataPoints: (item?.dataPoints == null || item?.dataPoints === '') ? 'N/A' : item?.dataPoints,
          workflowStage: item?.workflowStage,
          workflowStageDescription: item?.workflowStageDescription,
          workflowStatus: (item?.workflowStageDescription == null || item?.workflowStageDescription === '') ? 'N/A' : item?.workflowStageDescription,
          closureReasonDesc: (item?.closureReasonDesc == null || item?.closureReasonDesc === '') ? 'N/A' : item?.closureReasonDesc,
          workflowUsername: (item?.workflowUsername == null || item?.workflowUsername === '') ? 'N/A' : item?.workflowUsername,
          additionalData: item.additionalData ?? null
        };
        data.push(datum);
      });
      const pageCount = Math.ceil(itemCount / gridCriteria.pageSize);
      this.setState({ loading: false, data, gridCriteria: { ...gridCriteria, itemCount, pageCount, totalLimit: gridTotalLimit } });
    }
  };

  generateRequest = () => {
    const { searchCriteria, gridCriteria } = this.state;
    let sortDirection;
    if (gridCriteria.sortBy != null && gridCriteria.sortBy[0]) {
      if (gridCriteria.sortBy[0]?.desc != null) {
        sortDirection = gridCriteria.sortBy[0]?.desc ? 'desc' : 'asc';
      }
    } else {
      sortDirection = null;
    }
    return ({
      pageNumber: gridCriteria.pageIndex + 1,
      pageSize: gridCriteria.pageSize,
      totalLimit: gridCriteria.totalLimit,
      sortByField: gridCriteria.sortBy != null ? gridCriteria.sortBy[0]?.id : null,
      sortDirection,
      filterList: gridCriteria.filters,
      raisedWithin: searchCriteria.raisedWithin === '' ? null : searchCriteria.raisedWithin,
      legalEntity: !searchCriteria.legalEntity ? null : (searchCriteria.legalEntity?.id || searchCriteria.legalEntity),
      legalEntitySelected: searchCriteria.legalEntitySelected,
      accountNumber: !searchCriteria.accountNumber ? null : (searchCriteria.accountNumber?.id || searchCriteria.accountNumber),
      accountNumberSelected: searchCriteria.accountNumberSelected,
      customer: !searchCriteria.customer ? null : (searchCriteria.customer?.id || searchCriteria.customer),
      customerSelected: searchCriteria.customerSelected
    });
  };

  onSearchCriteriaChanged = (newSearchCriteria) => {
    const { searchCriteria, gridCriteria } = this.state;

    this.setState({ searchCriteria: { ...searchCriteria, ...newSearchCriteria }, gridCriteria: { ...gridCriteria, pageIndex: 0 } }, (() => {
      this.fetchData();
    }));
  };

  onGridCriteriaChanged = (newGridCriteria) => {
    this.setState({ gridCriteria: { ...newGridCriteria } }, () => this.fetchData());
  };

  onRowSelectionChanged = (rowSelections) => {
    const { gridCriteria } = this.state;
    this.setState({ gridCriteria: { ...gridCriteria, ...rowSelections } });
  };

  sendDataRequest = () => {
    const { dispatch } = this.props;
    const { searchCriteria, gridCriteria } = this.state;
    const request = this.generateRequest();

    if (searchCriteria.workflowType === 'open' || searchCriteria.workflowType === 'complete') {
      request.resolved = searchCriteria.workflowType === 'complete';
      dispatch(getCicAlertsByObject(request));
    } else {
      let sortDirection;
      if (gridCriteria.sortBy != null && gridCriteria.sortBy[0]) {
        if (gridCriteria.sortBy[0]?.desc != null) {
          sortDirection = gridCriteria.sortBy[0]?.desc ? 'desc' : 'asc';
        }
      } else {
        sortDirection = null;
      }

      const workflowRequest = {
        workflow: 'cic',
        pageNumber: request.pageNumber,
        pageSize: gridCriteria.pageSize,
        totalLimit: gridCriteria.totalLimit,
        sortByField: gridCriteria.sortBy != null ? gridCriteria.sortBy[0]?.id : null,
        sortDirection,
        stage: searchCriteria.workflowType,
        raisedWithin: searchCriteria.raisedWithin,
        legalEntity: !searchCriteria.legalEntity ? null : (searchCriteria.legalEntity?.id || searchCriteria.legalEntity),
        legalEntitySelected: searchCriteria.legalEntitySelected,
        accountNumber: !searchCriteria.accountNumber ? null : (searchCriteria.accountNumber?.id || searchCriteria.accountNumber),
        accountNumberSelected: searchCriteria.accountNumberSelected,
        customer: !searchCriteria.customer ? null : (searchCriteria.customer?.id || searchCriteria.customer),
        customerSelected: searchCriteria.customerSelected,
        filterList: gridCriteria.filters
      };

      switch (searchCriteria.workflowType) {
        case 'myalerts':
          dispatch(getUserCicAlertsByObject(workflowRequest));
          break;
        case 'initialreview':
          workflowRequest.stage = 'pendingreview';
          dispatch(getWorkflowCicAlertsByObject(workflowRequest));
          break;
        case 'expertreview':
          workflowRequest.stage = 'pendingexpertreview';
          dispatch(getWorkflowCicAlertsByObject(workflowRequest));
          break;
        case 'complete':
          workflowRequest.stage = 'complete';
          dispatch(getWorkflowCicAlertsByObject(workflowRequest));
          break;
        default:
          console.error(`Default case hit: ${ searchCriteria.workflowType }`);
      }
    }
  };

  downloadReport = () => {
    const { dispatch } = this.props;
    const { searchCriteria } = this.state;
    const request = this.generateRequest();
    request.resolved = searchCriteria.workflowType === 'complete';
    dispatch(getCicAlertReportFile(request));
  };

  fetchData = () => {
    const { gridCriteria, loading } = this.state;

    if (loading) {
      return;
    }

    clearTimeout(this.timer);
    if (this.isSearchCriteriaValid) {
      this.setState({ loading: true, gridCriteria: { ...gridCriteria, selectedItems: [] } }, () => {
        this.timer = setTimeout(() => {
          this.sendDataRequest();
        }, WAIT_INTERVAL);
      });
    }
  };

  // eslint-disable-next-line class-methods-use-this
  getChildRowLayout = childData => (<React.Fragment> {
    childData.accounts != null && <div className="child-data-item">
      <label>Accounts</label> <span>{ childData.accounts.map(a => <React.Fragment key={ a.item1 }>
        <a href={ `/account/${ a.item1 }` }>{ a.item2 }</a><br /></React.Fragment>) }</span></div>
      }
    { (!childData.accounts || childData.accounts.length === 0) && <div className="child-data-item">
      <label>Accounts</label> <span>N/A</span></div> } { childData.legalEntities != null
      && <div className="child-data-item"> <label>Legal Entites</label>
        <span>{ childData.legalEntities.map(l => <React.Fragment key={ l }>{ l }<br /></React.Fragment>) }</span></div> }
    { (!childData.legalEntities || childData.legalEntities.length === 0) && <div className="child-data-item">
      <label>Legal Entitis</label> <span>N/A</span></div> } </React.Fragment>);

  onWorkflowTypeChanged = (workflowType) => {
    const { permissions } = this.props;
    const { gridCriteria, searchCriteria } = this.state;

    if (workflowType === searchCriteria.workflowType) {
      return;
    }

    const columnDefs = CicColumnDefs(permissions, workflowType);
    this.setState({ loading: true, searchCriteria: { ...searchCriteria, workflowType }, gridCriteria: { ...gridCriteria, columnDefs, filters: [], sortBy: [] } }, () => this.sendDataRequest());
  };

  closeCicAlertModal = () => {
    const { dispatch } = this.props;
    this.sendDataRequest();
    dispatch(hideCicAlertModal(false));
    this.updateNotificationHeader();
  };

  getActions = () => {
    const { permissions } = this.props;

    return CicActions({
      permissions,
      takeAlert: cicAlert => this.onTakeAlert(cicAlert)
    });
  };

  openAssignCicToUserModal = (multipleItems) => {
    const { dispatch } = this.props;
    const selectedItemIds = Array.from(multipleItems, x => x.itemId);
    dispatch(showAssignCicToUserModal(selectedItemIds));
  };

  getItemsList = () => {
    const { cicAlertList } = this.props;
    return cicAlertList;
  };

  closeAssignCicToUserModal = () => {
    const { dispatch } = this.props;
    dispatch(hideAssignCicToUserModal());
  };

  processAssignToUsers = (_, user) => {
    const { dispatch } = this.props;
    const { gridCriteria } = this.state;
    const selectedItemIds = Array.from(gridCriteria.selectedItems, x => x.itemId);
    dispatch(assignCicsToUserWithCallback(selectedItemIds, user, () => this.fetchData()));
  };

  onRowExpandedStateChanged = (row, expand) => {
    const { dispatch } = this.props;

    if (expand) {
      dispatch(getCicAlertDetails(row.original.itemId, parseInt(row.id, 10)));
    } else {
      dispatch(clearCicAlertDetails(row.original.itemId, parseInt(row.id, 10)));
    }
  };

  showChangeOwnershipModal = (cicAlert) => {
    const { permissions } = this.props;

    if (cicAlert != null && cicAlert.workflowStageDescription != null && cicAlert.workflowUsername != null) {
      const wfStatus = cicAlert.workflowStageDescription.toLowerCase();
      const hasHigherPermission = AccessControl.hasPermission(permissions, [ActivityPerms.monitoringResolver]);

      if (wfStatus.includes('initial')) {
        return AccessControl.hasPermission(permissions, [ActivityPerms.monitoringResolver]) && hasHigherPermission;
      }

      if (wfStatus.includes('expert')) {
        return AccessControl.hasPermission(permissions, [ActivityPerms.monitoringExpertReviewer]);
      }
    }
    return false;
  };

  onTakeAlert = (cicAlert) => {
    const { dispatch, permissions } = this.props;
    const wfStatus = cicAlert.workflowStageDescription.toLowerCase();
    if (cicAlert && cicAlert.itemId != null) {
      if (Monitoring.isUserOwner(cicAlert.workflowUsername)) {
        dispatch(showCicAlertModal(cicAlert.itemId));
      } else if (wfStatus.includes('initial') && AccessControl.hasPermission(permissions, [ActivityPerms.fwfInitialReviewer])) {
        if (this.showChangeOwnershipModal(cicAlert)) {
          dispatch(showOwnershipModal({
            id: cicAlert.itemId,
            currentUser: cicAlert.workflowUsername,
            currentUserFullName: cicAlert.workflowUserFullName
          }));
        }
      } else if (wfStatus.includes('expert') && AccessControl.hasPermission(permissions, [ActivityPerms.fwfExpertReviewer])) {
        if (this.showChangeOwnershipModal(cicAlert)) {
          dispatch(showOwnershipModal({
            id: cicAlert.itemId,
            currentUser: cicAlert.workflowUsername,
            currentUserFullName: cicAlert.workflowUserFullName
          }));
        }
      } else if (wfStatus.includes('closed') && AccessControl.hasPermission(permissions, [ActivityPerms.monitoringResolver])) {
        dispatch(showCicAlertModal(cicAlert.itemId));
      } else {
        dispatch(showCicAlertModal(cicAlert.itemId));
      }
    }
  };

  updateNotificationHeader() {
    const { dispatch } = this.props;
    dispatch(getCicAlertCounts());
  }

  reAssignItem() {
    const { dispatch, changeOwnershipItem } = this.props;
    if (changeOwnershipItem != null && changeOwnershipItem.id != null) {
      dispatch(takeCicAlert(changeOwnershipItem.id));
      dispatch(hideOwnershipModal());
      dispatch(showCicAlertModal(changeOwnershipItem.id));
    }
  }

  continueFromOwnershipModal() {
    const { dispatch, changeOwnershipItem } = this.props;
    dispatch(hideOwnershipModal());
    dispatch(showCicAlertModal(changeOwnershipItem.id));
  }

  closeFromOwnershipModal() {
    const { dispatch } = this.props;
    dispatch(hideOwnershipModal());
  }

  render() {
    const {
      data,
      initialLoaded,
      loading,
      searchCriteria,
      gridCriteria,
      selectionEnabled
    } = this.state;
    const {
      isCicAlertModalShown,
      cicAlertId,
      isOwnershipModalShown,
      changeOwnershipItem,
      isAssignCicToUserModalShown,
      isGettingCicAlertXls
    } = this.props;
    return (
      <div className="landing-page monitoring">
        <SearchPanel
          searchCriteria={ searchCriteria }
          onSearchCriteriaChanged={ this.onSearchCriteriaChanged }
          onSearchClicked={ this.fetchData }
          actionButton={
            (searchCriteria.workflowType === 'open' || searchCriteria.workflowType === 'complete')
            && <AccessControl allowedPermissions={ [ActivityPerms.monitoringDownloadReport] }>
              <button
                id="monitoring-download-report-btn"
                type="button"
                title="Generate report"
                className="btn btn-secondary btn-download-report"
                onClick={ () => this.downloadReport() }
                disabled={ isGettingCicAlertXls }
              >
                <span className={ `icon ${ isGettingCicAlertXls ? 'loading' : 'icon-share' } mr` } />
                <span> Generate report</span>
              </button>
            </AccessControl>
          }
        />
        <div className="panel search-panel mt">
          <div className="panel-body results cic-results">
            <div className="form-tabs">
              <button
                id="workflow-mywork-btn"
                type="button"
                className={ `btn btn-${ searchCriteria.workflowType === 'myalerts' ? 'primary' : 'secondary' }` }
                onClick={ () => this.onWorkflowTypeChanged('myalerts') }
              >Assigned to Me</button>
              <AccessControl allowedPermissions={ [ActivityPerms.monitoringViewUnassigned] }>
                <button
                  id="workflow-initialreview-btn"
                  type="button"
                  className={ `btn btn-${ searchCriteria.workflowType === 'initialreview' ? 'primary' : 'secondary' }` }
                  onClick={ () => this.onWorkflowTypeChanged('initialreview') }
                >Pending Initial Review</button>
              </AccessControl>
              <AccessControl allowedPermissions={ [ActivityPerms.monitoringExpertReviewer] }>
                <button
                  id="workflow-expertreview-btn"
                  type="button"
                  className={ `btn btn-${ searchCriteria.workflowType === 'expertreview' ? 'primary' : 'secondary' }` }
                  onClick={ () => this.onWorkflowTypeChanged('expertreview') }
                >Pending Expert Review</button>
              </AccessControl>
              <AccessControl allowedPermissions={ [ActivityPerms.monitoringViewResolved] }>
                <button
                  id="workflow-complete-btn"
                  type="button"
                  className={ `btn btn-${ searchCriteria.workflowType === 'complete' ? 'primary' : 'secondary' }` }
                  onClick={ () => this.onWorkflowTypeChanged('complete') }
                >Closed</button>
              </AccessControl>
              <AccessControl allowedPermissions={ [ActivityPerms.monitoringViewUnresolved] }>
                <button
                  id="workflow-allforms-btn"
                  type="button"
                  className={ `btn btn-${ searchCriteria.workflowType === 'open' ? 'primary' : 'secondary' }` }
                  onClick={ () => this.onWorkflowTypeChanged('open') }
                >Open Alerts</button>
              </AccessControl>
              { selectionEnabled && <ActionSelect
                selectedItems={ gridCriteria.selectedItems }
                openAssignToUserModal={ this.openAssignCicToUserModal }
              /> }
            </div>
            <ReactTable
              data={ data }
              columns={ CicColumnDefs }
              gridCriteria={ gridCriteria }
              onGridCriteriaChanged={ this.onGridCriteriaChanged }
              onRowSelectionChanged={ this.onRowSelectionChanged }
              childRowPropertyName="additionalData"
              enableExpandCollapse
              onRowExpandedStateChanged={ this.onRowExpandedStateChanged }
              getChildRowLayout={ childData => this.getChildRowLayout(childData) }
              actions={ this.getActions() }
              enableSelection
              loading={ loading }
              initialLoaded={ initialLoaded }
              className={ selectionEnabled ? 'workflow' : '' }
            />
          </div>
        </div>
        { isCicAlertModalShown && <CicAlertModal
          id={ cicAlertId }
          isShown={ isCicAlertModalShown }
          onCancel={ () => this.closeCicAlertModal() }
        /> }
        { changeOwnershipItem != null && <ChangeOwnershipModal
          isShown={ isOwnershipModalShown }
          onAssign={ () => this.reAssignItem() }
          onContinueWithoutAssign={ () => this.continueFromOwnershipModal() }
          onCancel={ () => this.closeFromOwnershipModal() }
          workflowRecord={ changeOwnershipItem }
        /> }
        <AssignToUserModal
          isShown={ isAssignCicToUserModalShown }
          onCancel={ () => this.closeAssignCicToUserModal() }
          onConfirmAssign={ (cicAlertList, user) => this.processAssignToUsers(cicAlertList, user) }
          getItemsList={ () => this.getItemsList() }
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  const {
    auth: {
      permissions,
      settings
    },
    forms: {
      gridTotalLimit,
    },
    monitoring: {
      cicAlertsList,
      cicAlertsListCount,
      searchCriteria,
      gridCriteria,
      isCicAlertModalShown,
      cicAlertId,
      refreshPage,
      isAssignCicToUserModalShown,
      cicAlertList,
      isGettingCicAlertXls
    },
    workflow: {
      isFetchingQueues,
      isGettingAccounts,
      isGettingCustomers,
      isOwnershipModalShown,
      changeOwnershipItem
    }
  } = state;
  return {
    permissions,
    settings,
    gridTotalLimit,
    cicAlertsList,
    cicAlertsListCount,
    isFetchingQueues,
    isGettingAccounts,
    isGettingCustomers,
    isCicAlertModalShown,
    cicAlertId,
    refreshPage,
    isOwnershipModalShown,
    changeOwnershipItem,
    isAssignCicToUserModalShown,
    cicAlertList,
    isGettingCicAlertXls,
    initSearchCriteria: searchCriteria,
    initGridCriteria: gridCriteria
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withContainerErrorBoundary(Monitoring, 'Monitoring'));
